
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { request } from "../../utils/api";
import { getSessionTenantId } from "../../utils/tenantContext";
import { getEvaluationDefaultValues, getRoleConfig } from "./evaluation.constants";
import { getValidationSchema } from "./evaluation.validation";

import { ROLE_PAYLOAD_MAP, mapBackendDataToFrontend } from "./payloadMappings";
import { formatSignature, getLocalSystemDate, toIsoDate } from "../../utils/dateUtils";
import { isEvaluationReadOnly, isSurveyorUser } from "../../utils/formPermissions";
import { resolveHrDocumentReturnPath } from "../../utils/applicationFormNavigation";

// Helper to format payload based on role
const getPayloadForRole = (role, data, currentUser) => {
    const normalizedRole = String(role || "").toUpperCase();
    const isRN = normalizedRole === 'RN' || normalizedRole === 'LVN' || normalizedRole === 'HHA';

    const basePayload = {
        tenantId: getSessionTenantId() ?? currentUser.tenantId,
        employeeId: Number.parseInt(data.employeeName || data.employeeId, 10),
        reviewerId: currentUser.id,
        position: data.jobTitle,
        evaluationDate: toIsoDate(data.evaluationDate) || new Date().toISOString(),
        evaluationType: data.reviewType || "Annual", // Default if missing
        hireDate: toIsoDate(data.hireDate) || new Date().toISOString(),
        reviewerSignature: data.reviewerSignature,
        reviewerSignedAt: toIsoDate(data.reviewerSignedAt) || new Date().toISOString(),
        reviewerName: data.reviewerName,
        // Common organizational fields (mapped from various frontend IDs)
        daysTardy: parseInt(
            data.organizational2_daysTardy ||
            data.organizational3_daysTardy ||
            data.daysTardy ||
            0
        ),
        daysAbsent: parseInt(
            data.organizational2_daysAbsent ||
            data.organizational3_daysAbsent ||
            data.daysAbsent ||
            0
        ),
    };

    // Collect comments and goals from all possible pages (up to 3)
    const comments = [];
    const targetedGoals = [];
    [1, 2, 3].forEach(page => {
        const commentVal = data[`dutiesCommentPage${page}`];
        if (commentVal) {
            comments.push({
                selectedComment: "Comment",
                commentText: commentVal,
                sortOrder: page - 1
            });
        }

        const goalVal = data[`targetedGoalsPage${page}`];
        if (goalVal) {
            targetedGoals.push({
                selectedTargetedGoal: "Goal",
                targetedGoalText: goalVal,
                sortOrder: page - 1
            });
        }
    });

    // Handle RN/LVN/HHA comments and goals
    if (isRN) {
        // Map to specific endpoint keys for RN/LVN/HHA
        const mapping = ROLE_PAYLOAD_MAP[normalizedRole];
        const ratingFields = {};
        if (mapping) {
            Object.entries(mapping).forEach(([frontendId, backendKey]) => {
                const val = data[frontendId];
                if (val) ratingFields[backendKey] = parseInt(val);
            });
        }

        return {
            ...basePayload,
            ...ratingFields,
            evaluationPeriod: "90 Day",
            comments: comments.length > 0 ? comments : (data.comments || []),
            targetedGoals: targetedGoals.length > 0 ? targetedGoals : undefined
        };
    }

    // PT/PTA Specific Payload
    if (normalizedRole === 'PT' || normalizedRole === 'PTA') {
        const dutyItems = Array.from({ length: 25 }, (_, i) => {
            const dutyIndex = i;
            const dutyKey = `duty${i + 1}`;
            const rating = data[dutyKey];
            if (!rating) return null;
            return {
                dutyIndex: dutyIndex,
                dutyDescription: `Duty ${i + 1}`,
                rating: parseInt(rating)
            };
        }).filter(Boolean);

        return {
            ...basePayload,
            overallComments: data.overallComments || "Performance Summary",
            comments: comments.length > 0 ? comments : (data.comments || []),
            dutyItems: dutyItems
        };
    }

    // Role-specific mapping
    const mapping = ROLE_PAYLOAD_MAP[normalizedRole];
    if (mapping) {
        const ratingFields = {};

        // Iterate over the mapping to find matching data in form
        Object.entries(mapping).forEach(([frontendId, backendKey]) => {
            const val = data[frontendId];
            if (val) {
                // Ensure rating is number
                ratingFields[backendKey] = parseInt(val);
            }
        });

        const payload = {
            ...basePayload,
            ...ratingFields,
            comments: comments.length > 0 ? comments : (data.comments || []),
            targetedGoals: targetedGoals.length > 0 ? targetedGoals : undefined
        };
        if (normalizedRole === "OA" || normalizedRole === "OFFICE ASSISTANT" || normalizedRole === "OFFICE-ASSISTANT") {
            payload.autoFillRating = data.selectedKey ? parseInt(data.selectedKey) : undefined;
            payload.evaluationPeriod = data.reviewType === "Probationary" ? "90 Day" : "Annual";
        }
        return payload;
    }

    return {
        ...basePayload,
        comments: comments.length > 0 ? comments : (data.comments || []),
        targetedGoals: targetedGoals.length > 0 ? targetedGoals : undefined
    };
};


export const useEvaluationForm = (selectedRole, evaluationId = null) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitLockRef = useRef(false);
    const [currentEvaluationId, setCurrentEvaluationId] = useState(evaluationId);

    // Sync instance evaluationId with state if it changes from props
    useEffect(() => {
        setCurrentEvaluationId(evaluationId);
    }, [evaluationId]);
    const navigate = useNavigate();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);

    // Reset page to 1 on role/eval change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedRole, evaluationId]);

    // Signature Modal State
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [reviewerSignatureName, setReviewerSignatureName] = useState("");
    const [submitAfterSign, setSubmitAfterSign] = useState(false);
    const [fromCurrentStatus, setFromCurrentStatus] = useState(null);

    // Get current user for validation logic
    let currentUser = {};
    try {
        currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
    } catch (e) {
        console.error("Error parsing current user", e);
    }

    const isAdmin = currentUser.roleId === 1 || currentUser.role?.id === 1 ||
        (typeof currentUser.role === 'string' && currentUser.role.toLowerCase() === 'admin') ||
        (currentUser.role?.name?.toLowerCase() === 'admin')
    // (currentUser.roleId === 12); // Surveyor also has admin-like access to evaluations

    // Create dynamic schema and defaults based on role
    const validationSchema = getValidationSchema(selectedRole, currentUser);
    const defaultValues = getEvaluationDefaultValues(selectedRole);

    const form = useForm({
        resolver: zodResolver(validationSchema),
        defaultValues: defaultValues,
        mode: "onBlur", // Validate on blur
    });

    // Reset form when role changes
    useEffect(() => {
        form.reset(getEvaluationDefaultValues(selectedRole));
    }, [selectedRole, form]);

    // Function to prefill form with backend data (applying reverse mapping)
    const prefillData = (role, backendData) => {
        try {
            const mappedData = mapBackendDataToFrontend(role, backendData);

            // Merge with existing defaults if needed, or just reset
            // We might want to keep some default values if backend data is partial
            // But usually reset is safer for "edit" mode
            form.reset(mappedData);
        } catch (error) {
            console.error("Error prefilling data:", error);
            toast.error("Failed to load evaluation data");
        }
    };

    // New: Load evaluation data by ID (automatically determining endpoint from role)
    const loadEvaluation = async (evaluationId) => {
        if (!evaluationId || !selectedRole) return;

        try {
            const config = getRoleConfig(selectedRole);
            const endpoint = `${config.apiEndpoint}/${evaluationId}`;

            const response = await request(endpoint);

            if (response.status === 1 && response.result) {
                // Determine if result is nested (common pattern: result.evaluation or just result)
                // Adjust based on actual backend response structure. 
                // Assuming result is the evaluation object directly or contains it.
                // Safest to check keys or assume root of result if standard.
                const data = response.result.evaluation || response.result;
                setFromCurrentStatus(data.status)
                if (data.id) setCurrentEvaluationId(data.id);
                prefillData(selectedRole, data);
            } else {
                console.error("Failed to load:", response);
                toast.error(response.message || "Failed to load evaluation");
            }
        } catch (error) {
            console.error("Load error:", error);
            toast.error("Error loading evaluation");
        }
    };

    // Prefill target employee when opened from HR Documents (pending evaluation)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const targetUserId = params.get("userId");
        if (!targetUserId || currentEvaluationId) return;

        form.setValue("employeeName", targetUserId, { shouldValidate: false });
    }, [selectedRole, currentEvaluationId, form]);

    // Determine Read-Only state (surveyors always view-only)
    const watchedEmployeeName = form.watch("employeeName");
    const isReadOnly =
        isSurveyorUser(currentUser) ||
        isEvaluationReadOnly(currentUser, fromCurrentStatus, watchedEmployeeName);

    const submitData = async (data, actionType = "save") => {
        if (submitLockRef.current) return;
        submitLockRef.current = true;
        setIsSubmitting(true);
        try {
            const currentUserStr = localStorage.getItem("currentUser");
            const currentUser = currentUserStr ? JSON.parse(currentUserStr) : {};

            // Determine role and config
            const role = selectedRole || 'PT'; // Default or passed prop
            const config = getRoleConfig(role);

            // Validation: E-Signature is required for both Save and Submit (Backend Requirement)
            if (!data.reviewerSignature || data.reviewerSignature.trim() === "") {
                toast.error("Please sign the evaluation before saving or submitting.");
                return;
            }

            // Map form data to backend payload
            const payload = getPayloadForRole(role, data, currentUser);

            payload.status = actionType === 'completed' ? 'completed' : (fromCurrentStatus || 'pending');

            // Determine endpoint and method
            const endpoint = currentEvaluationId
                ? `${config.apiEndpoint}/${currentEvaluationId}`
                : config.apiEndpoint;
            const method = currentEvaluationId ? "PUT" : "POST";

            // Send to backend
            const response = await request(endpoint, {
                method: method,
                body: JSON.stringify(payload)
            });
            if (response.status === 1) {
                const saved =
                    response.result?.evaluation ||
                    response.result?.evaluationRn ||
                    response.result?.evaluationLvn ||
                    response.result?.evaluationHha ||
                    response.result?.evaluationPtPta ||
                    response.result?.evaluationOtOta ||
                    response.result?.evaluationSt ||
                    response.result?.evaluationMsw ||
                    response.result?.evaluationOa ||
                    response.result;
                const savedId = saved?.id || response.result?.id;
                if (savedId) {
                    setCurrentEvaluationId(savedId);
                }
                const successMsg = actionType === "save" ? "Evaluation saved successfully" : "Evaluation submitted successfully";
                toast.success(response.message || successMsg);

                // Redirect back instead of reload
                const params = new URLSearchParams(window.location.search);
                const returnUrl = resolveHrDocumentReturnPath(
                    params,
                    "evaluation-forms",
                );

                if (returnUrl) {
                    navigate(returnUrl);
                } else {
                    // Fallback to history if editing, otherwise role selection
                    if (currentEvaluationId || savedId) {
                        navigate('/evaluation/history');
                    } else {
                        navigate('/evaluation');
                    }
                }
                // Force a page refresh as requested by user
                window.location.reload();
            } else {
                toast.error(response.message || "Failed to save evaluation");
            }

        } catch (error) {
            console.error("Submission error", error);
            toast.error(error.message || "Failed to save evaluation");
        } finally {
            submitLockRef.current = false;
            setIsSubmitting(false);
        }
    };

    // Signature Handlers
    const openESignModal = (shouldSubmit = false) => {
        if (isReadOnly) return; // Prevent signing if read-only
        setSubmitAfterSign(shouldSubmit);

        let initialName = "";
        try {
            const currentUserStr = localStorage.getItem("currentUser");
            if (currentUserStr) {
                const user = JSON.parse(currentUserStr);
                if (user.firstName) {
                    initialName = user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName;
                }
            }
        } catch (e) {
            console.error("Error parsing current user", e);
        }

        // Fallback to reviewer name form field if local storage fails (or empty)
        if (!initialName) {
            initialName = form.watch("reviewerName") || "";
        }

        setReviewerSignatureName(initialName);
        setShowSignatureModal(true);
    };

    const saveSignature = () => {
        if (!reviewerSignatureName || reviewerSignatureName.trim() === "") {
            return;
        }

        // Format the electronic signature
        const formattedSignature = formatSignature(reviewerSignatureName.trim());

        // Update form values
        form.setValue("reviewerSignature", formattedSignature, { shouldDirty: true });
        form.setValue("reviewerSignatureImage", "", { shouldDirty: true });
        form.setValue("reviewerSignatureTimestamp", new Date().toISOString(), { shouldDirty: true });
        form.setValue("reviewerSignatureSigner", reviewerSignatureName.trim(), { shouldDirty: true });

        // Update Date field visible in UI
        const today = getLocalSystemDate();
        form.setValue("reviewerSignedAt", today, { shouldDirty: true });

        // Ensure evaluationDate is not lost (belt and suspenders)
        const currentEvalDate = form.getValues("evaluationDate");
        if (!currentEvalDate) {
            form.setValue("evaluationDate", today, { shouldDirty: true });
        }

        setShowSignatureModal(false);
        setReviewerSignatureName("");

        if (submitAfterSign) {
            // Trigger submit immediately as 'submit' type
            setTimeout(() => {
                form.handleSubmit((data) => submitData(data, "submit"))();
            }, 50);
        }
    };

    const closeESignModal = () => {
        setShowSignatureModal(false);
        setReviewerSignatureName("");
    };

    // Custom submit wrapper to pass the type
    const handleCustomSubmit = (actionType) => {
        return form.handleSubmit((data) => submitData(data, actionType));
    };

    return {
        form,
        isSubmitting,
        submit: handleCustomSubmit, // Now returns a function usage: submit("save")() or submit("submit")()
        submitData, // Exposed raw submit function for manual handling (e.g. from footer)
        // Ideally we return a helper that returns the event handler
        // But handleSubmit returns (e) => ...
        // So submit("save") returns (e) => ...
        prefillData, // Exposed helper
        loadEvaluation, // Exposed helper for fetching
        // State
        currentPage,
        setCurrentPage,
        isReadOnly, // EXPOSED
        isAdmin,    // EXPOSED
        signatureState: {
            showSignatureModal,
            reviewerSignatureName,
            setReviewerSignatureName,
            submitAfterSign // Expose this for UI button label
        },
        // Handlers
        openESignModal,
        saveSignature,
        closeESignModal
    };
};
